"use client";

import { FC, useMemo } from "react";
import Image from "next/image";
import { Image as DatoImage, ResponsiveImageType } from "react-datocms";
import { useTranslations } from "next-intl";
import classNames from "classnames";

import { IconType, ILink, IOfficeAddress } from "@types";
import { OfficeLocationRecord } from "@graphql/types/graphql";

import { Button } from "components/atoms/Button/Button";
import { Icon } from "components/atoms/Icon/Icon";
import { TranslatedLink } from "components/atoms/TranslatedLink/TranslatedLink";

import s from "./ImageTeaser.module.scss";

interface Props {
  externalLink?: string;
  link?: ILink;
  image?: ResponsiveImageType;
  urlImage?: string;
  title: string;
  description?: any;
  address?: string;
  addresses?: (OfficeLocationRecord & { distance?: number })[];
  buttonLabel: string;
}

export const ImageTeaser: FC<Props> = ({
  externalLink,
  link,
  image,
  urlImage,
  title,
  description,
  address,
  addresses,
  buttonLabel,
}) => {
  const t = useTranslations("common");
  const formatOfficeAddress = (officeAddress?: IOfficeAddress | null) => {
    if (!officeAddress) {
      return "";
    }

    const streetLine = [officeAddress.street, officeAddress.housenumber]
      .filter(Boolean)
      .join(" ")
      .trim();
    const cityLine = [officeAddress.postcode, officeAddress.city].filter(Boolean).join(" ").trim();
    const segments = [streetLine, cityLine, officeAddress.country].filter(
      (segment): segment is string => Boolean(segment && segment.trim().length > 0),
    );

    return segments.join(", ");
  };

  const addressLimit = 3;
  const sortedAddresses: OfficeLocationRecord[] | undefined = useMemo(
    () =>
      addresses
        ? [...addresses].sort((a, b) => (a.distance && b.distance ? a.distance - b.distance : 0))
        : undefined,
    [addresses],
  );
  const limitedAddresses: OfficeLocationRecord[] | undefined = useMemo(
    () => sortedAddresses?.slice(0, addressLimit),
    [sortedAddresses],
  );
  const tooManyAddresses = useMemo(
    () => addresses && addresses?.length > addressLimit,
    [addresses],
  );

  const content = useMemo(
    () => (
      <>
        {image && <DatoImage data={image} />}
        {urlImage && (
          <Image src={urlImage} alt={title} width={1280} height={720} className={s.Image} />
        )}
        <div className={s.Content}>
          <h3 className={s.Title}>{title}</h3>
          {description && (
            <div
              className={classNames(s.Description, "prose")}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          {address && (
            <div className={s.AddressWrapper}>
              <div className={s.AddressIcon}>
                <Icon name={IconType.Location} size="l" />{" "}
              </div>
              <div className={s.Address}>{address}</div>
            </div>
          )}
          {limitedAddresses?.map((item) => (
            <div className={s.AddressWrapper} key={item.id}>
              <div className={s.AddressIcon}>
                <Icon name={IconType.Location} size="l" />{" "}
              </div>
              <div className={s.Address}>
                {formatOfficeAddress(item.officeAddress as IOfficeAddress | null | undefined)}
              </div>
            </div>
          ))}
          {addresses && tooManyAddresses && (
            <div className={s.AddressWrapper}>
              <div className={s.AddressIcon}>
                <Icon name={IconType.Location} size="l" />{" "}
              </div>
              <div className={s.Address}>
                ... {t("and")} {addresses.length - addressLimit}{" "}
                {addresses.length - addressLimit === 1 ? t("other-location") : t("other-locations")}
              </div>
            </div>
          )}
        </div>
        <div className={s.Bottom}>
          <Button label={buttonLabel} theme="textual" icon={IconType.CaretFilled} />
        </div>
      </>
    ),
    [
      image,
      urlImage,
      title,
      description,
      address,
      addresses,
      buttonLabel,
      limitedAddresses,
      tooManyAddresses,
      t,
    ],
  );

  if (externalLink) {
    return (
      <a
        href={externalLink}
        target="_blank"
        rel="noopener noreferrer external"
        className={s.ImageTeaser}
      >
        {content}
      </a>
    );
  }

  if (link && sortedAddresses !== undefined && sortedAddresses.length >= 2) {
    return (
      <TranslatedLink
        link={{
          href: link.href,
          slug: `${link.slug}#${sortedAddresses[0].slug}`,
        }}
        className={s.ImageTeaser}
      >
        {content}
      </TranslatedLink>
    );
  }

  if (link) {
    return (
      <TranslatedLink link={link} className={s.ImageTeaser}>
        {content}
      </TranslatedLink>
    );
  }

  return <div className={s.ImageTeaser}>{content}</div>;
};
